import { debounce, type SxProps, TextField, type Theme, Tooltip } from '@mui/material';
import { DocumentReference, updateDoc } from 'firebase/firestore';
import React, { KeyboardEvent, useEffect, useMemo, useState } from 'react';

type Props<T> = {
    objRef: DocumentReference
    label?: string
    valKey: keyof T
    obj: T
    tooltip?: string
    sx?: SxProps<Theme>
    autoFocus?: boolean
    multiline?: boolean
    onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement | HTMLDivElement>) => void
}

export function DebouncedFirebaseTextField<T extends { [key in keyof T]: unknown }>(props: Props<T>) {
    const {
        objRef,
        label,
        valKey,
        obj,
        tooltip,
        sx,
        autoFocus = false,
        multiline = false,
        onKeyDown = () => {},
    } = props;

    const [value, setValue] = useState<string>(obj[valKey] as string);

    useEffect(() => {
        setValue(obj[valKey] as string);
    }, [obj, valKey]);

    const debouncedUpdate = useMemo(() => {
        return debounce((val: string, debRef: DocumentReference, debKey: keyof T) => updateDoc(debRef, { [debKey]: val }), 500);
    }, [])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        void debouncedUpdate(e.target.value, objRef, valKey);
    }

    return (
        <Tooltip title={tooltip} arrow>
            <TextField
                label={label}
                onChange={handleChange}
                value={value}
                variant='standard'
                sx={[
                    {
                        m: 1,
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
                autoFocus={autoFocus}
                onKeyDown={onKeyDown}
                multiline={multiline}
            />
        </Tooltip>
    )
}